import { useEffect, useState } from 'react'
import {
  Users, BookOpen, Video, TrendingUp, Calendar,
  Award, Eye, Plus, ArrowUpRight, ArrowDownRight,
  Activity, Zap, DollarSign, MoreHorizontal
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import EnrollmentChart from '../../components/admin/charts/EnrollmentChart'
import RevenueChart from '../../components/admin/charts/RevenueChart'
import UserGrowthChart from '../../components/admin/charts/UserGrowthChart'

interface DashboardStats {
  totalStudents: number
  totalCourses: number
  upcomingLiveSessions: number
  totalRevenue: number
  studentGrowth: number
  courseGrowth: number
  sessionGrowth: number
  revenueGrowth: number
}

interface RecentActivity {
  id: string
  type: 'enrollment' | 'payment' | 'session' | 'course'
  message: string
  timestamp: string
  amount?: number
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    totalStudents: 0,
    totalCourses: 0,
    upcomingLiveSessions: 0,
    totalRevenue: 0,
    studentGrowth: 0,
    courseGrowth: 0,
    sessionGrowth: 0,
    revenueGrowth: 0
  })
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)

  // Chart data
  const [enrollmentData, setEnrollmentData] = useState<{ month: string; enrollments: number }[]>([])
  const [revenueData, setRevenueData] = useState<{ month: string; revenue: number }[]>([])
  const [userGrowthData, setUserGrowthData] = useState<{ month: string; newUsers: number; totalUsers: number }[]>([])

  useEffect(() => {
    fetchDashboardData()
    fetchChartData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data.stats || {
          totalStudents: 0,
          totalCourses: 0,
          upcomingLiveSessions: 0,
          totalRevenue: 0,
          studentGrowth: 0,
          courseGrowth: 0,
          sessionGrowth: 0,
          revenueGrowth: 0
        })
        setRecentActivities(data.recentActivities || [])
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchChartData = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/chart-data', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setEnrollmentData(data.enrollmentData || [])
        setRevenueData(data.revenueData || [])
        setUserGrowthData(data.userGrowthData || [])
      }
    } catch (error) {
      console.error('Error fetching chart data:', error)
    }
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: `₹${stats.totalRevenue.toLocaleString()}`,
      change: stats.revenueGrowth,
      icon: DollarSign,
      color: 'text-emerald-600',
      bg: 'bg-emerald-100 dark:bg-emerald-900/20',
      trendColor: 'text-emerald-600'
    },
    {
      title: 'Active Students',
      value: stats.totalStudents,
      change: stats.studentGrowth,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      trendColor: 'text-blue-600'
    },
    {
      title: 'Total Courses',
      value: stats.totalCourses,
      change: stats.courseGrowth,
      icon: BookOpen,
      color: 'text-violet-600',
      bg: 'bg-violet-100 dark:bg-violet-900/20',
      trendColor: 'text-violet-600'
    },
    {
      title: 'Live Sessions',
      value: stats.upcomingLiveSessions,
      change: stats.sessionGrowth,
      icon: Video,
      color: 'text-rose-600',
      bg: 'bg-rose-100 dark:bg-rose-900/20',
      trendColor: 'text-rose-600'
    }
  ]

  const quickActions = [
    {
      label: 'New Course',
      desc: 'Create a new course',
      icon: Plus,
      action: () => navigate('/admin/courses/new'),
      color: 'bg-blue-500'
    },
    {
      label: 'Schedule Session',
      desc: 'Setup live class',
      icon: Calendar,
      action: () => navigate('/admin/live-sessions/new'),
      color: 'bg-emerald-500'
    },
    {
      label: 'Add Instructor',
      desc: 'Onboard new teacher',
      icon: Award,
      action: () => navigate('/admin/instructors/new'),
      color: 'bg-violet-500'
    },
    {
      label: 'View Payments',
      desc: 'Check transactions',
      icon: Eye,
      action: () => navigate('/admin/payments'),
      color: 'bg-rose-500'
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-t-4 border-b-4 border-rose-500 animate-spin"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-rose-500">
            <Activity size={24} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          const isPositive = stat.change >= 0
          const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight

          return (
            <div
              key={index}
              className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-dark-700 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3.5 rounded-2xl ${stat.bg} group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                <div className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${isPositive
                    ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                  <TrendIcon size={14} />
                  <span>{Math.abs(stat.change)}%</span>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-1">
                  {stat.value}
                </h3>
                <p className="text-sm text-charcoal-500 dark:text-charcoal-400 font-medium">
                  {stat.title}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main Chart Section */}
        <div className="xl:col-span-2 space-y-8">
          {/* Revenue & Enrollment Charts */}
          <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">Revenue Overview</h3>
                <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Monthly earnings performance</p>
              </div>
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors">
                <MoreHorizontal size={20} className="text-charcoal-400" />
              </button>
            </div>
            <RevenueChart data={revenueData} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">Enrollment Trends</h3>
                <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Student course registrations</p>
              </div>
              <EnrollmentChart data={enrollmentData} />
            </div>
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">User Growth</h3>
                <p className="text-sm text-charcoal-500 dark:text-charcoal-400">New platform users</p>
              </div>
              <UserGrowthChart data={userGrowthData} />
            </div>
          </div>
        </div>

        {/* Sidebar Section: Quick Actions & Recent Activity */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center gap-2 mb-6">
              <Zap className="text-amber-500" size={20} />
              <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">Quick Actions</h3>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 dark:hover:bg-dark-700/50 border border-transparent hover:border-gray-100 dark:hover:border-dark-600 transition-all duration-300 group text-left"
                  >
                    <div className={`p-3 rounded-xl text-white shadow-lg shadow-gray-200 dark:shadow-none ${action.color} group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <span className="block font-semibold text-charcoal-900 dark:text-white text-sm">
                        {action.label}
                      </span>
                      <span className="text-xs text-charcoal-500 dark:text-charcoal-400">
                        {action.desc}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Activity className="text-rose-500" size={20} />
                <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">Recent Activity</h3>
              </div>
            </div>

            <div className="space-y-6">
              {recentActivities.length > 0 ? (
                recentActivities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="relative pl-6 pb-6 last:pb-0 border-l border-gray-100 dark:border-dark-700 last:border-0">
                    <div className={`absolute left-0 top-0 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white dark:border-dark-800 ${activity.type === 'payment' ? 'bg-emerald-500' :
                        activity.type === 'enrollment' ? 'bg-blue-500' :
                          activity.type === 'session' ? 'bg-violet-500' :
                            'bg-rose-500'
                      }`}></div>

                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="text-sm font-medium text-charcoal-900 dark:text-white leading-none mb-1.5">
                          {activity.message}
                        </p>
                        <p className="text-xs text-charcoal-500 dark:text-charcoal-400">
                          {activity.timestamp}
                        </p>
                      </div>
                      {activity.amount && (
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-lg">
                          +₹{activity.amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-charcoal-500 dark:text-charcoal-400">No recent activities</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}